import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import {
	getCoordinationList,
	unassignCoordination
} from '../../../../../api/nora/programs';

import { useAuthState } from '../../../../../contexts/AuthContext';
import {
	useProgramState,
	useProgramAction
} from '../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Alert from '../../../../Alert';
import CoordinationModal from './CoordinationModal';
import FirstAndLastName from '../../../../Table/Cells/FirstAndLastName';
import HelpModal from '../../../../Shared/HelpModal';
import SectionHeader from '../../../../Shared/SectionHeader';
import Table from '../../../../Table';
import TableRowActions from '../../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../../Table/filterFunctions/multiWordFilterFn';

const CoordinationTab = ( { activeIndex } ) => {
	const history = useHistory();

	const { user: { permissions } } = useAuthState();
	const { program } = useProgramState();
	const {
		setCoordination,
		setCoordinationOptions
	} = useProgramAction();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ coordinationModal, setCoordinationModal ] = useState( false );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ coordinatorToDelete, setCoordinatorToDelete ] = useState( {} );

	useEffect( () => {
		setCoordinationOptions( program?.id );
	}, [
		program,
		setCoordinationOptions
	] );

	const retrieveCoordinationList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Coordination List...' );

		try {
			const data = await getCoordinationList( program?.id );

			setCoordination( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		program,
		setCoordination,
		setApiErrorAlert
	] );

	const addCoordinator = useCallback( () => {
		setCoordinationModal( true );
	}, [] );

	const coordinationActions = useMemo( () => [
		{
			action: addCoordinator,
			disable: !permissions.editPrograms.canExecute,
			icon: 'add',
			permission: permissions.editPrograms.isAvailable,
			text: 'Add Coordinator'
		}
	], [
		addCoordinator,
		permissions
	] );

	const closeCoordinationModal = useCallback( () => {
		setCoordinationModal( false );
	}, [] );

	const firstAndLastNameCell = useCallback( ( { row } ) => <FirstAndLastName row={ row.original } />, [] );

	const onView = useCallback( ( { id } ) => {
		history.push( {
			pathname: `/administration/users/${ id }`,
			state: {
				text: '\u2190\u00A0\u00A0Back to Program',
				to: {
					pathname: `/operations/programs/${ program?.id }`,
					state: { activeIndex }
				}
			}
		} );
	}, [
		activeIndex,
		history,
		program
	] );

	const onDelete = useCallback( participant => {
		setCoordinatorToDelete( participant );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewUsers.canExecute,
				icon: 'search',
				permission: permissions.viewUsers.isAvailable,
				text: 'View User'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.editPrograms.canExecute,
				icon: 'times',
				permission: permissions.editPrograms.isAvailable,
				text: 'Remove User'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDelete,
		onView,
		permissions
	] );

	const columns = useMemo( () => [
		{
			id: 'name',
			accessor: row => `${ row.firstName }${ row.lastName }`,
			Header: 'Name',
			filter: multiWordFilterFn,
			Cell: firstAndLastNameCell,
			minWidth: 60
		},
		{
			accessor: 'organization',
			Header: 'Organization',
			filter: multiWordFilterFn,
			minWidth: 60
		}
	], [ firstAndLastNameCell ] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewUsers.isAvailable || permissions.editPrograms.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const handleUnassignCoordinator = useCallback( async () => {
		setInfobarLoader( 'Retrieving Coordination List...' );

		try {
			await unassignCoordination( program?.id, coordinatorToDelete.id );
			await retrieveCoordinationList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		program,
		coordinatorToDelete.id,
		retrieveCoordinationList,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to remove <strong>{ `${ coordinatorToDelete.firstName } ${ coordinatorToDelete.lastName }` }</strong> from the list?</span>, [ coordinatorToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	return (
		<>
			<SectionHeader
				actions={ coordinationActions }
				title={
					<>
						<span>Program Coordination</span>
						<HelpModal
							id={ 9 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			{ program?.coordinators?.length
				? <Table
					columns={ renderColumns }
					data={ program.coordinators }
					showCount={ program.coordinators.length > 10 }
					showEmptyRows={ false }
					showPagination={ program.coordinators.length > 10 }
				/>
				: null
			}
			<CoordinationModal
				closeModal={ closeCoordinationModal }
				open={ coordinationModal }
				retrieveCoordinationList={ retrieveCoordinationList }
			/>
			<Alert
				action={ handleUnassignCoordinator }
				name="unassign-coordination-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</>
	);
};

CoordinationTab.propTypes = { activeIndex: PropTypes.number };

export default CoordinationTab;
